import { ArrowLeft, CloudOff, Ellipsis, ExternalLink, Radar } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router'
import { isApiError } from '../api/client'
import { useCollections, usePauseSource, useResumeSource, useSource, useSourcePosts } from '../api/queries'
import type { MetricView, Source } from '../api/types'
import { Avatar } from '../components/Avatar'
import { Button } from '../components/Button'
import { EmptyState } from '../components/EmptyState'
import { FeedTile, TileSkeleton } from '../components/feed/FeedTile'
import { FeedTextGrid, FeedVideoGrid } from '../components/feed/FeedGrid'
import { TextCard, TextCardSkeleton } from '../components/feed/TextCard'
import { Card, Section } from '../components/Section'
import { Chip, Select } from '../components/Select'
import { AddSourceDialog } from '../components/sources/AddSourceDialog'
import { OwnTag, RemoveDialog, SourceDialog, StateTag, statusExplanation, type SourceAction } from '../components/sources/SourceDetails'
import { Spinner } from '../components/Spinner'
import { toast } from '../components/Toast'
import { AuthorInsights } from '../components/source-insights/AuthorInsights'
import { openExternal } from '../lib/download'
import { FEED_PERIODS, FORMAT_LABEL, FORMATS, sortLabel, sortsFor } from '../lib/feedFormat'
import { setFeedOrder } from '../lib/feedNav'
import { PLATFORM_LABEL, formatAgo, formatCount, formatShortDate, handleLabel, sourceInput } from '../lib/format'
import { useViewIsActive, useViewState } from '../lib/navigation'
import { sourceContentFilters } from '../lib/sourceContent'
import { useBack } from '../lib/useBack'
import { MetricViewSelect } from '../components/MetricViewSelect'
import { metricView } from '../lib/measurement'

export function SourcePage() {
  const id = Number(useParams().id)
  const source = useSource(id)
  const back = useBack('/sources')
  const valid = Number.isSafeInteger(id) && id > 0
  const notFound = !valid || isApiError(source.error, 404)
  return (
    <>
      <div className="mb-4 hidden desk:block">
        <Button variant="ghost" onClick={back}><ArrowLeft size={15} />Назад</Button>
      </div>
      {notFound ? (
        <EmptyState icon={Radar} title="Источник не найден"><Button className="mt-3" onClick={back}>К источникам</Button></EmptyState>
      ) : source.isPending ? (
        <div className="flex justify-center py-12" data-view-ready="false"><Spinner /></div>
      ) : source.isError ? (
        <EmptyState icon={CloudOff} title="Не удалось загрузить источник"><Button className="mt-3" onClick={() => void source.refetch()}>Повторить</Button></EmptyState>
      ) : (
        <SourceDetail key={source.data.id} source={source.data} />
      )}
    </>
  )
}

export function SourceExplanation({ source, view }: { source: Source; view?: MetricView }) {
  if (source.platform === 'instagram' && view !== 'legacy' && !source.from_analysis && (source.status === 'found' || source.status === 'quiet')) return null
  return <p className={`mt-3 text-sm text-pretty ${source.status === 'error' ? 'text-danger' : 'text-muted'}`}>{statusExplanation(source)}</p>
}

function SourceDetail({ source: s }: { source: Source }) {
  const [params] = useSearchParams()
  const [resolvedView, setResolvedView] = useState<MetricView | undefined>()
  const name = handleLabel(s.platform, s.handle)
  const collections = useCollections()
  const pause = usePauseSource()
  const resume = useResumeSource()
  const active = useViewIsActive()
  const [dialog, setDialog] = useViewState<'actions' | 'remove' | 'restore' | null>(`source.${s.id}.dialog`, null)
  const act = (action: SourceAction) => {
    if (action === 'details') { setDialog('actions'); return }
    if (action === 'remove') { setDialog('remove'); return }
    if (action === 'restore') { setDialog('restore'); return }
    setDialog(null)
    const mutation = action === 'pause' ? pause : resume
    mutation.mutate(s.id, {
      onSuccess: () => toast(action === 'pause' ? `${name} на паузе` : `${name} снова собирается`),
      onError: (err) => toast(
        isApiError(err, 409) && err.detail === 'limit_reached'
          ? `Потолок активных ${PLATFORM_LABEL[s.platform]}: поставьте кого-то на паузу`
          : isApiError(err, 409) && err.detail === 'reddit_off'
            ? 'Reddit выключен: нет ключей Reddit API'
            : 'Не получилось изменить сбор источника',
        { error: true, ms: 4000 },
      ),
    })
  }
  const groups = collections.data?.filter((c) => s.collection_ids.includes(c.id)) ?? []
  return (
    <>
      <Card className="mb-5 p-4 desk:p-5">
        <div className="flex items-start gap-3 desk:gap-4">
          <Avatar key={s.avatar_url} name={s.title ?? s.handle} src={s.avatar_url ?? null} size={72} />
          <div className="min-w-0 flex-1">
            <p className="text-xs text-muted">{PLATFORM_LABEL[s.platform]}</p>
            <h1 className="page-title mt-0.5">{s.title || name}</h1>
            {s.title && <p className="mt-1.5 break-all text-sm text-muted">{name}</p>}
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <StateTag source={s} />
              {s.own_board_id != null && <OwnTag boardId={s.own_board_id} />}
            </div>
          </div>
          <Button size="icon" variant="ghost" disabled={pause.isPending || resume.isPending} aria-label={`Действия с ${name}`} onClick={() => setDialog('actions')}>
            {pause.isPending || resume.isPending ? <Spinner size={16} /> : <Ellipsis size={20} />}
          </Button>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted">
          <span>{s.followers === null ? 'Подписчики —' : `${formatCount(s.followers)} подписчиков`}</span>
          <span>{s.last_polled_at ? `Проверен ${formatAgo(s.last_polled_at)}` : 'Ждёт первого обхода'}</span>
          {s.added_at && <span>Добавлен {formatShortDate(s.added_at)}</span>}
        </div>
        <SourceExplanation source={s} view={metricView(params) ?? resolvedView} />
        {s.url && <Button className="mt-4 w-full desk:w-auto" onClick={() => openExternal(s.url ?? '')}><ExternalLink size={16} />Открыть профиль в {PLATFORM_LABEL[s.platform]}</Button>}
        {groups.length > 0 && <p className="mt-3 text-xs text-muted">Подборки: {groups.map((c) => c.name).join(', ')}</p>}
      </Card>
      <AuthorInsights source={s} onMetricView={setResolvedView} fallback={<SourceContent source={s} onMetricView={setResolvedView} />} />
      {active && dialog === 'actions' && <SourceDialog source={s} showCardLink={false} onAction={act} onClose={() => setDialog(null)} />}
      {active && dialog === 'remove' && <RemoveDialog source={s} onClose={() => setDialog(null)} />}
      {active && dialog === 'restore' && <AddSourceDialog initialInput={sourceInput(s.platform, s.handle)} onClose={() => setDialog(null)} />}
    </>
  )
}

function SourceContent({ source: s, onMetricView }: { source: Source; onMetricView: (view: MetricView) => void }) {
  const [params, setParams] = useSearchParams()
  const { format, sorts, query, unsupportedPeriod } = sourceContentFilters(params, s.platform)
  const posts = useSourcePosts(s.id, query)
  const resolvedView = query.metric_view ?? posts.data?.pages[0]?.metric_view
  useEffect(() => { if (resolvedView) onMetricView(resolvedView) }, [resolvedView, onMetricView])
  const [pageCount, setPageCount] = useViewState(`source.${s.id}.pages.${JSON.stringify(query)}`, 1)
  const loadedPages = posts.data?.pages.length ?? 0
  useEffect(() => { if (loadedPages > pageCount) setPageCount(loadedPages) }, [loadedPages, pageCount, setPageCount])
  useEffect(() => {
    if (loadedPages > 0 && loadedPages < pageCount && posts.hasNextPage && !posts.isFetching && !posts.isError) void posts.fetchNextPage()
  }, [loadedPages, pageCount, posts.hasNextPage, posts.isFetching, posts.isError, posts.fetchNextPage])
  const restoringPages = !posts.isError && (posts.isPending || (loadedPages < pageCount && posts.hasNextPage))
  const items = useMemo(() => posts.data?.pages.flatMap((p) => p.items) ?? [], [posts.data])
  const [now] = useState(() => Date.now())
  const { hasNextPage, isFetchingNextPage, fetchNextPage } = posts
  useEffect(() => {
    setFeedOrder(items.map((item) => item.id), hasNextPage && !isFetchingNextPage ? () => void fetchNextPage() : null, items)
  }, [items, hasNextPage, isFetchingNextPage, fetchNextPage])
  const update = (patch: Record<string, string | number | null>) => setParams((prev) => {
    const next = new URLSearchParams(prev)
    for (const [key, value] of Object.entries(patch)) {
      if (value === null || value === '') next.delete(key)
      else next.set(key, String(value))
    }
    return next
  }, { replace: true })
  const video = format === 'video'
  const filtered = query.days !== undefined || query.only_outliers
  const emptyHint = s.from_analysis
    ? 'Автор не отслеживается. Чтобы собирать новые публикации, добавьте его в источники.'
    : s.state === 'paused'
      ? 'Источник на паузе. Чтобы собрать новые публикации, верните его в сбор.'
      : s.state === 'removed'
        ? 'Источник убран из сбора. Его сохранённая история доступна в этой карточке.'
        : s.status === 'error'
          ? 'Сбор источника завершился ошибкой. После восстановления новые публикации появятся здесь.'
          : 'Публикации появятся после ближайшего обхода источника.'
  const Grid = video ? FeedVideoGrid : FeedTextGrid
  return (
    <Section title="Собранный контент">
      <p className="mb-3 text-sm text-muted">Сохранённые нами публикации автора. По умолчанию — за всё время, включая посты без залёта.</p>
      {unsupportedPeriod && <p className="mb-3 text-sm text-muted">Архив сейчас показан за всё время. Выберите доступный период ниже или повторите загрузку аналитики.</p>}
      {s.platform === 'instagram' && <div className="mb-3 flex gap-2" aria-label="Формат контента">
        {FORMATS.map((next) => <Chip key={next} active={format === next} onClick={() => update({ format: next, s: sortsFor(next, s.platform).includes(query.sort) ? params.get('s') : null })}>{FORMAT_LABEL[next]}</Chip>)}
      </div>}
      <div data-view-scroll={`source.${s.id}.filters`} className="-mx-[var(--gutter)] mb-4 flex items-center gap-2 overflow-x-auto px-[var(--gutter)] pb-1 [scrollbar-width:none] desk:mx-0 desk:px-0">
        {s.platform === 'instagram' && <MetricViewSelect value={query.metric_view ?? posts.data?.pages[0]?.metric_view ?? 'legacy'} onChange={(value) => update({ metric_view: value })} />}
        <Select label="Период публикации" value={query.days ?? ''} onChange={(e) => update({ d: e.target.value || null, period_days: null, all_time: null, date_from: null, date_to: null })}>
          <option value="">За всё время</option>
          {FEED_PERIODS.map((p) => <option key={p.days} value={p.days}>{p.label}</option>)}
          {query.days !== undefined && !FEED_PERIODS.some((p) => p.days === query.days) && <option value={query.days}>За {query.days} дн.</option>}
        </Select>
        <Select label="Сортировка" value={query.sort} onChange={(e) => update({ s: e.target.value === 'new' ? null : e.target.value })}>
          {sorts.map((sort) => <option key={sort} value={sort}>{sortLabel(sort, format, s.platform)}</option>)}
        </Select>
        <label className="inline-flex h-9 shrink-0 items-center gap-2 text-sm desk:text-[13px]">
          <input type="checkbox" checked={query.only_outliers} onChange={(e) => update({ outliers: e.target.checked ? '1' : null })} className="size-4 accent-fg" />Только залёты ×2+
        </label>
      </div>
      <div data-view-ready={restoringPages ? 'false' : 'true'}>
        {posts.isPending ? <Grid>{Array.from({ length: video ? 12 : 6 }, (_, i) => video ? <TileSkeleton key={i} /> : <TextCardSkeleton key={i} />)}</Grid>
          : posts.isError && !posts.data ? <EmptyState icon={CloudOff} title="Не удалось загрузить контент"><Button className="mt-3" onClick={() => void posts.refetch()}>Повторить</Button></EmptyState>
            : items.length === 0 ? <EmptyState icon={Radar} title={filtered ? 'С этими фильтрами публикаций нет' : 'Собранных публикаций пока нет'}>{filtered ? 'Попробуйте другой период или отключите «Только залёты ×2+».' : emptyHint}</EmptyState>
              : <>
                <Grid>{items.map((item) => video ? <FeedTile key={item.id} item={item} now={now} isNew={false} /> : <TextCard key={item.id} item={item} now={now} isNew={false} />)}</Grid>
                <div className="mt-5 flex flex-col items-center gap-2">
                  <p className="text-xs text-muted">Показано публикаций: {items.length}</p>
                  {posts.isFetchNextPageError && <p className="text-sm text-danger">Не удалось загрузить следующую страницу.</p>}
                  {posts.hasNextPage && <Button disabled={posts.isFetchingNextPage} onClick={() => void posts.fetchNextPage()}>{posts.isFetchingNextPage && <Spinner size={14} />}Показать ещё</Button>}
                </div>
              </>}
      </div>
    </Section>
  )
}
